import { getDailyPhoneHours, getStartupName, type OnboardingAnswers } from './onboardingConfig';

/** Quiz presentation is shared; analytics and backend isolation are selected separately. */
export function isGuidedPaidQuiz(search: string) {
  return ['fc1', 'tst1'].includes(new URLSearchParams(search).get('qz') ?? '');
}

export function getGuidedReclaimCopy(answers: OnboardingAnswers) {
  const hours = getDailyPhoneHours(answers);
  return `You spend ${hours} ${hours === 1 ? 'hour' : 'hours'} a day on your phone. Even reclaiming ${hours > 1 ? 'one of those hours' : '15 minutes'} would make more room for ${getStartupName(answers)}.`;
}

export function getGuidedReaction(screen: number, answers: OnboardingAnswers, fallback: string) {
  const name = answers.userName?.trim();
  const company = getStartupName(answers);
  if (screen === 6 && answers.startupName) {
    return `${company}. I like it${name ? `, ${name}` : ''}. Now let's find a little more time to build it.`;
  }
  if (screen === 10) {
    const hours = getDailyPhoneHours(answers);
    return hours <= 2
      ? 'A pretty good starting point. Even a little less scrolling leaves room for something you care about.'
      : `${hours} hours a day adds up. We don't need to win all of them back. Let's start with one focused session.`;
  }
  if (screen === 12 && answers.focusKillers?.length) {
    const distractions = answers.focusKillers;
    if (distractions.includes('All of the above')) return 'A lot competing for your attention. We’ll start with one quiet block of time.';
    if (distractions.length === 1 && distractions[0] === 'My own brain') return 'A busy brain. I get it. A short session and one clear task is a good place to start.';
    return `${distractions.join(' and ')}. Noted. Let's give your next focus session some breathing room.`;
  }
  if (screen === 15 && answers.dailySessions && answers.focusDuration) {
    const sessions = parseInt(answers.dailySessions, 10);
    const minutes = parseInt(answers.focusDuration, 10);
    if (Number.isFinite(sessions) && Number.isFinite(minutes)) {
      const qualifier = answers.dailySessions.includes('+') || answers.focusDuration.includes('+') ? 'At least ' : '';
      return `${qualifier}${sessions * minutes} minutes a day for ${company}. A small routine with something real behind it.`;
    }
  }
  if (screen === 24) {
    if (answers.commitmentLevel?.includes('not sure') || answers.commitmentLevel?.startsWith('Curious')) {
      return 'You don’t have to have it all figured out. Try one session and see how it feels.';
    }
    return `Let's do this${name ? `, ${name}` : ''}. I'll get the office ready. You bring the focus.`;
  }
  return fallback;
}

export function getGuidedPrompt(screen: number, answers: OnboardingAnswers) {
  const name = answers.userName?.trim();
  switch (screen) {
    case 2: return answers.entryIdentity === 'scroller'
      ? 'You caught yourself scrolling. That’s a good place to start.'
      : 'You bring the ideas. Let’s make room to work on them.';
    case 5: return 'I’m Bob, your virtual co-founder. What should I call you?';
    case 6: return `Good to meet you${name ? `, ${name}` : ''}. Every company starts somewhere.`;
    case 8: return getGuidedReaction(6, answers, 'A little context helps me get to know you.');
    case 9: return 'No judgement. Your phone’s Screen Time can help with this one.';
    case 10: return 'Small daily habits take up a surprising amount of time.';
    case 11: return getGuidedReaction(10, answers, 'One less scroll. One more thing finished.');
    case 12: return 'What keeps pulling you away? Pick as many as you need.';
    case 13: return getGuidedReaction(12, answers, 'Let’s work with your energy, not against it.');
    case 14: return `${answers.sharpestTime || 'Your best time of day'} is a good start. Let’s make it a routine.`;
    case 15: return 'Pick a session you’d actually finish on a busy day.';
    case 16: return getGuidedReaction(15, answers, `This is starting to look like your plan${name ? `, ${name}` : ''}.`);
    case 24: return 'Big plans start with one small commitment. I’m in if you are.';
    case 21: return `Putting your answers together for ${getStartupName(answers)}.`;
    case 28: return `Your plan is ready${name ? `, ${name}` : ''}.`;
    default: return 'One step at a time. We’ve got this.';
  }
}
